import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm, useWatch, type FieldValues, type Path, type UseFormReturn } from 'react-hook-form'

import { Alert } from '@/components/feedback/Alert'
import { FieldError } from '@/components/feedback/FieldError'
import { useToastStack } from '@/components/feedback/toast-stack'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { formatDateTime } from '@/lib/format'
import { fieldErrorProps } from '@/lib/form/field-error'
import { assignValues, useRecordEditor, type RecordEditor } from '@/lib/form/use-record-editor'
import { isoToIstanbul, istanbulToIso, normalizeTime } from '@/lib/hr'
import { messages } from '@/lib/messages'
import { toProblem } from '@/lib/problem'
import { queryKeys } from '@/lib/query-keys'
import { hrApi, type HrRequestDetail, type HrRequestResubmit } from '@/services/http/hr'
import { refreshRequestViews, trackRequestResult, useMovementCheck } from '@/features/hr/hr-actions'
import { useResubmitVersion } from '@/features/hr/use-resubmit-version'
import { LeaveFields, MovementFields, SubmitConfirm } from '@/features/hr/request-fields'
import {
  approveDisabledReason,
  lastRevision,
  requestSummary,
  targetCopy,
} from '@/features/hr/request-copy'
import {
  cancelResubmitSchema,
  fieldMessages,
  leaveFormValue,
  leaveResubmitSchema,
  leaveServerFields,
  leaveValues,
  MOVEMENT_SERVER_FIELDS,
  movementResubmitSchema,
  sameLeave,
  type CancelResubmitValue,
  type LeaveResubmitValue,
  type MovementResubmitValue,
} from '@/features/hr/request-value'
import { useAuthStore } from '@/stores/auth-store'

const t = messages.hr
const r = t.approvals

type Decision = 'approve' | 'revise' | 'cancel' | 'retry' | 'resolve' | 'withdraw'

/** Talep kararı; sonrası ilgili görünümler tazelenir, gönderim izlenir. */
function useDecision(id: string, onDone?: () => void) {
  const queryClient = useQueryClient()
  const toastApi = useToastStack()
  return useMutation({
    mutationFn: (input: { decision: Decision; message?: string }) =>
      hrApi.decideRequest(id, input.decision, input.message),
    onSuccess: (detail, input) => {
      queryClient.setQueryData(queryKeys.hr.request(id), detail)
      refreshRequestViews(queryClient, detail)
      const titles: Record<Decision, string> = {
        approve: targetCopy(detail).approved,
        revise: r.revised,
        cancel: r.cancelled,
        retry: r.retried,
        resolve: r.resolved,
        withdraw: r.withdrawn,
      }
      toastApi.toast({ type: 'success', title: titles[input.decision] })
      if (input.decision === 'approve' || input.decision === 'retry') {
        trackRequestResult(detail, toastApi, (latest) => {
          queryClient.setQueryData(queryKeys.hr.request(id), latest)
          refreshRequestViews(queryClient, latest)
        })
      }
      onDone?.()
    },
    onError: (error) => {
      toastApi.toast({
        type: 'error',
        title: r.actionFailed,
        description: toProblem(error).detail ?? undefined,
      })
    },
  })
}

/** Revizyondaki talepte neden en üstte: kim, ne zaman, ne istedi. */
export function RevisionNotice({ request }: { request: HrRequestDetail }) {
  const revision = lastRevision(request)
  if (!revision) return null
  return (
    <Alert tone="warning" title={r.revisionReason}>
      <p className="whitespace-pre-line">{revision.message}</p>
      <p className="text-text-muted mt-1 text-xs">
        {r.revisionBy(revision.actor.name ?? t.unknown, formatDateTime(revision.created_at))}
      </p>
    </Alert>
  )
}

/** Onayın son bakışı: talebin özeti ve hedef sistem. */
function approveLines(request: HrRequestDetail): [string, string][] {
  const summary = requestSummary(request)
  return [
    [r.person, `${request.personel_adi ?? t.unknown} · ${request.sicilno}`],
    [r.columns.tur, r.kind[request.kind]],
    [r.columns.tip, summary.tip],
    [r.columns.zaman, summary.zaman],
    [r.descriptionLabel, request.description],
  ]
}

/** Gerekçe zorunlu karar penceresi (iptal, geri çekme). */
function ReasonDialog({
  open,
  title,
  body,
  label,
  placeholder,
  confirmLabel,
  pending,
  onOpenChange,
  onConfirm,
}: {
  open: boolean
  title: string
  body: string
  label: string
  placeholder: string
  confirmLabel: string
  pending: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (reason: string) => void
}) {
  const [reason, setReason] = useState('')
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{body}</DialogDescription>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-1.5">
          <Label htmlFor="hr-reason-dialog">{label}</Label>
          <Textarea
            id="hr-reason-dialog"
            value={reason}
            maxLength={2000}
            placeholder={placeholder}
            onChange={(event) => setReason(event.target.value)}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t.actions.cancel}
          </Button>
          <Button
            variant="danger"
            disabled={!reason.trim() || pending}
            onClick={() => onConfirm(reason.trim())}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function DecisionPanel({ request }: { request: HrRequestDetail }) {
  const canApprove = useAuthStore((state) => state.hasPermission('hr.request:approve'))
  const [message, setMessage] = useState('')
  const [confirmApprove, setConfirmApprove] = useState(false)
  const [confirmCancel, setConfirmCancel] = useState(false)
  const decide = useDecision(request.id, () => setMessage(''))
  const open = request.status === 'onay_bekliyor' || request.status === 'revizyonda'
  // Onaylanmış ama aksiyonu hâlâ kuyrukta bekleyen talep de iptal edilebilir (kuyruk da iptal olur).
  if (!canApprove || (!open && !request.can_cancel)) return null
  const disabledReason = approveDisabledReason(request)
  return (
    <section className="border-border flex flex-col gap-2 border-t pt-4">
      <Label htmlFor="hr-decision-message">{r.message}</Label>
      <Textarea
        id="hr-decision-message"
        value={message}
        maxLength={2000}
        placeholder={r.messagePlaceholder}
        onChange={(event) => setMessage(event.target.value)}
      />
      <div className="flex flex-wrap gap-2">
        {request.status === 'onay_bekliyor' && (
          <>
            <Button
              disabled={!request.can_approve || decide.isPending}
              onClick={() => setConfirmApprove(true)}
            >
              {r.approve}
            </Button>
            <Button
              variant="secondary"
              disabled={!message.trim() || decide.isPending}
              title={message.trim() ? undefined : r.messageRequired}
              onClick={() => decide.mutate({ decision: 'revise', message })}
            >
              {r.revise}
            </Button>
          </>
        )}
        <Button
          variant="danger"
          disabled={!request.can_cancel || decide.isPending}
          onClick={() => setConfirmCancel(true)}
        >
          {r.cancel}
        </Button>
      </div>
      {request.status === 'onay_bekliyor' && disabledReason && (
        <p className="text-text-muted text-xs">{disabledReason}</p>
      )}
      <SubmitConfirm
        open={confirmApprove}
        title={r.approveConfirmTitle}
        body={targetCopy(request).approveConfirmBody}
        lines={approveLines(request)}
        pending={decide.isPending}
        confirmLabel={r.approve}
        onOpenChange={setConfirmApprove}
        onConfirm={() =>
          decide.mutate(
            { decision: 'approve', message },
            { onSettled: () => setConfirmApprove(false) },
          )
        }
      />
      <ReasonDialog
        key={String(confirmCancel)}
        open={confirmCancel}
        title={r.cancel}
        body={targetCopy(request).cancelConfirm}
        label={r.cancelReason}
        placeholder={r.cancelReasonPlaceholder}
        confirmLabel={r.cancel}
        pending={decide.isPending}
        onOpenChange={setConfirmCancel}
        onConfirm={(reason) =>
          decide.mutate(
            { decision: 'cancel', message: reason },
            { onSuccess: () => setConfirmCancel(false) },
          )
        }
      />
    </section>
  )
}

/** Talebi açan, karar verilmemiş kendi talebini gerekçeyle geri çeker. */
export function WithdrawPanel({ request }: { request: HrRequestDetail }) {
  const [open, setOpen] = useState(false)
  const decide = useDecision(request.id)
  if (!request.can_withdraw) return null
  return (
    <section className="border-border flex flex-col gap-2 border-t pt-4">
      <p className="text-text-secondary text-sm">{r.withdrawHint}</p>
      <Button variant="secondary" className="w-fit" onClick={() => setOpen(true)}>
        {r.withdraw}
      </Button>
      <ReasonDialog
        key={String(open)}
        open={open}
        title={r.withdraw}
        body={targetCopy(request).cancelConfirm}
        label={r.withdrawReason}
        placeholder={r.withdrawPlaceholder}
        confirmLabel={r.withdraw}
        pending={decide.isPending}
        onOpenChange={setOpen}
        onConfirm={(reason) =>
          decide.mutate(
            { decision: 'withdraw', message: reason },
            { onSuccess: () => setOpen(false) },
          )
        }
      />
    </section>
  )
}

/** DİA aksiyonu hataya düşen onaylı talebi aynı parametrelerle yeniden kuyruğa koyar. */
export function RetryPanel({ request }: { request: HrRequestDetail }) {
  const decide = useDecision(request.id)
  if (!request.can_retry) return null
  return (
    <section className="border-border flex flex-col gap-2 border-t pt-4">
      <p className="text-danger text-sm">{targetCopy(request).retryHint}</p>
      <Button
        className="w-fit"
        disabled={decide.isPending}
        onClick={() => decide.mutate({ decision: 'retry' })}
      >
        {targetCopy(request).retry}
      </Button>
    </section>
  )
}

/** Sonucu bilinmeyen (`tutarsiz`) talep: hedef sistemde kontrol edilip gerekçeyle kapatılır. */
export function ResolvePanel({ request }: { request: HrRequestDetail }) {
  const [message, setMessage] = useState('')
  const decide = useDecision(request.id, () => setMessage(''))
  if (!request.can_resolve) return null
  return (
    <section className="border-border flex flex-col gap-2 border-t pt-4">
      <p className="text-warning text-sm">{r.resolveHint}</p>
      <Label htmlFor="hr-resolve-message">{r.message}</Label>
      <Textarea
        id="hr-resolve-message"
        value={message}
        maxLength={2000}
        placeholder={r.resolvePlaceholder}
        onChange={(event) => setMessage(event.target.value)}
      />
      <Button
        className="w-fit"
        disabled={!message.trim() || decide.isPending}
        onClick={() => decide.mutate({ decision: 'resolve', message })}
      >
        {r.resolve}
      </Button>
    </section>
  )
}

export function ResubmitPanel({ request }: { request: HrRequestDetail }) {
  if (!request.can_resubmit) return null
  // Değişecek değer olmayan talepler (izin iptali, hareket silme): yalnız açıklama ve yanıt.
  if (request.kind === 'izin_iptal' || request.kind === 'giris_cikis_sil') {
    return <CancelResubmitPanel request={request} />
  }
  return request.leave_new ? (
    <LeaveResubmitPanel request={request} requested={request.leave_new} />
  ) : (
    <MovementResubmitPanel request={request} />
  )
}

/**
 * Yeniden gönderme formunun kaydetme akışı: sürüm kapısı (409 → `version.alert`), 422 alan hataları
 * alanın altında. Panel detay Sheet'inin içindedir, kendi kapatma yolu yoktur; kirliyken uygulama içi
 * bağlantıdan çıkış `editor.dialog` ile sorulur. Başarıda talep güncellenir ve panel kapanır
 * (`can_resubmit` düşer).
 */
function useResubmitEditor<TValues extends { message: string } & FieldValues>({
  request,
  form,
  build,
  serverFields,
}: {
  request: HrRequestDetail
  form: UseFormReturn<TValues>
  /** Talebe özgü gövde alanları; `message` ve `version` burada eklenir. */
  build: (values: TValues) => Omit<HrRequestResubmit, 'message' | 'version'>
  serverFields: Partial<Record<string, Path<TValues>>>
}) {
  const queryClient = useQueryClient()
  const toastApi = useToastStack()
  const version = useResubmitVersion(request)
  const editor = useRecordEditor({
    form,
    submit: (values) =>
      hrApi.resubmitRequest(request.id, {
        ...build(values),
        message: values.message.trim(),
        version: version.current(),
      }),
    onSaved: (detail) => {
      queryClient.setQueryData(queryKeys.hr.request(request.id), detail)
      refreshRequestViews(queryClient, detail)
      toastApi.toast({ type: 'success', title: r.resubmitted })
    },
    version,
    serverFields,
    failedTitle: r.actionFailed,
  })
  return { version, editor }
}

/** Panelin üst kısmı: başlık, çakışma uyarısı ve onay diyalogları. */
function ResubmitHeader({
  version,
  editor,
}: {
  version: ReturnType<typeof useResubmitVersion>
  editor: RecordEditor
}) {
  return (
    <>
      <h3 className="text-sm font-medium">{r.resubmit}</h3>
      {version.alert}
      {version.dialog}
      {editor.dialog}
    </>
  )
}

function LeaveResubmitPanel({
  request,
  requested,
}: {
  request: HrRequestDetail
  requested: NonNullable<HrRequestDetail['leave_new']>
}) {
  const form = useForm<LeaveResubmitValue>({
    resolver: zodResolver(leaveResubmitSchema),
    defaultValues: { ...leaveFormValue(requested, request.description), message: '' },
  })
  const value = useWatch({ control: form.control }) as LeaveResubmitValue
  const { version, editor } = useResubmitEditor({
    request,
    form,
    build: (values) => ({ leave: leaveValues(values), description: values.description.trim() }),
    serverFields: {
      ...leaveServerFields(value.saatlik),
      message: 'message',
      description: 'description',
    },
  })
  const current = request.leave_old ?? null
  // Değişmemiş izin talebi gönderilmez (backend de reddeder).
  const unchanged = current !== null && sameLeave(leaveValues(value), current)
  const errors = fieldMessages(form.formState.errors)
  return (
    <section className="border-border flex flex-col gap-3 border-t pt-4">
      <ResubmitHeader version={version} editor={editor} />
      <LeaveFields
        idPrefix="hr-resubmit"
        value={value}
        current={current}
        errors={errors}
        onChange={(next) => assignValues(form, { ...value, ...next })}
      />
      <ResubmitMessage
        value={value.message}
        error={errors.message}
        onChange={(message) => assignValues(form, { ...value, message })}
      />
      <Button
        className="w-fit"
        disabled={unchanged || editor.saving}
        onClick={() => void editor.save()}
      >
        {r.resubmit}
      </Button>
    </section>
  )
}

function ResubmitMessage({
  value,
  error,
  onChange,
}: {
  value: string
  error?: string
  onChange: (next: string) => void
}) {
  const id = 'hr-resubmit-message'
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{r.message}</Label>
      <Textarea
        id={id}
        value={value}
        maxLength={2000}
        placeholder={r.messagePlaceholder}
        {...fieldErrorProps(id, error)}
        onChange={(event) => onChange(event.target.value)}
      />
      <FieldError id={id} message={error} />
    </div>
  )
}

/** İptal talebinde değişecek değer yok: yalnız açıklama ve yanıt. */
function CancelResubmitPanel({ request }: { request: HrRequestDetail }) {
  const form = useForm<CancelResubmitValue>({
    resolver: zodResolver(cancelResubmitSchema),
    defaultValues: { description: request.description, message: '' },
  })
  const value = useWatch({ control: form.control }) as CancelResubmitValue
  const { version, editor } = useResubmitEditor({
    request,
    form,
    build: (values) => ({ description: values.description.trim() }),
    serverFields: { message: 'message', description: 'description' },
  })
  const errors = fieldMessages(form.formState.errors)
  const id = 'hr-resubmit-description'
  return (
    <section className="border-border flex flex-col gap-3 border-t pt-4">
      <ResubmitHeader version={version} editor={editor} />
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={id}>{r.descriptionLabel}</Label>
        <Textarea
          id={id}
          value={value.description}
          maxLength={2000}
          {...fieldErrorProps(id, errors.description)}
          onChange={(event) => assignValues(form, { ...value, description: event.target.value })}
        />
        <FieldError id={id} message={errors.description} />
      </div>
      <ResubmitMessage
        value={value.message}
        error={errors.message}
        onChange={(message) => assignValues(form, { ...value, message })}
      />
      <Button className="w-fit" disabled={editor.saving} onClick={() => void editor.save()}>
        {r.resubmit}
      </Button>
    </section>
  )
}

function MovementResubmitPanel({ request }: { request: HrRequestDetail }) {
  const initial = request.occurred_at ? isoToIstanbul(request.occurred_at) : { date: '', time: '' }
  const form = useForm<MovementResubmitValue>({
    resolver: zodResolver(movementResubmitSchema),
    defaultValues: {
      girisCikis: request.giris_cikis ?? 'G',
      date: initial.date,
      time: initial.time,
      description: request.description,
      message: '',
    },
  })
  const value = useWatch({ control: form.control }) as MovementResubmitValue
  const { version, editor } = useResubmitEditor({
    request,
    form,
    build: (values) => ({
      giris_cikis: values.girisCikis,
      occurred_at: istanbulToIso(values.date, normalizeTime(values.time)),
      description: values.description.trim(),
    }),
    serverFields: {
      ...MOVEMENT_SERVER_FIELDS,
      message: 'message',
      description: 'description',
    },
  })
  const check = useMovementCheck(request.sicilno, value, request.hareket_id)
  const blocked = (check?.errors.length ?? 0) > 0
  const errors = fieldMessages(form.formState.errors)
  return (
    <section className="border-border flex flex-col gap-3 border-t pt-4">
      <ResubmitHeader version={version} editor={editor} />
      <MovementFields
        idPrefix="hr-resubmit"
        value={value}
        check={check}
        errors={errors}
        onChange={(next) => assignValues(form, { ...value, ...next })}
      />
      <ResubmitMessage
        value={value.message}
        error={errors.message}
        onChange={(message) => assignValues(form, { ...value, message })}
      />
      <Button
        className="w-fit"
        disabled={blocked || editor.saving}
        onClick={() => void editor.save()}
      >
        {r.resubmit}
      </Button>
    </section>
  )
}
